import { VIEW_ICON } from './nav-icons';
import { type View, VIEW_LABEL, MOBILE_VIEWS } from '../lib/views';

interface Props {
  view: View;
  setView: (v: View) => void;
  pendingChanges?: number;
  draftNewsCount?: number;
}

/**
 * Bottom tab bar pour mobile (<md). Limité à 5 tabs par contrainte de place.
 * La liste est figée dans lib/views.ts (MOBILE_VIEWS) et peut être ajustée
 * sans toucher au composant.
 *
 * Badge accent (amber) sur News quand draftNewsCount > 0, badge founders sur
 * la view qui a pendingChanges (Investors directory par défaut).
 */
export function BottomNav({ view, setView, pendingChanges, draftNewsCount }: Props) {
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-line-soft shadow-pop"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="grid grid-cols-5 max-w-md mx-auto">
        {MOBILE_VIEWS.map((v) => {
          const Icon = VIEW_ICON[v];
          let badge: number | undefined;
          let accentBadge = false;
          if (v === 'investors-directory') badge = pendingChanges;
          if (v === 'news') {
            badge = draftNewsCount;
            accentBadge = true;
          }
          return (
            <Tab
              key={v}
              active={view === v}
              onClick={() => setView(v)}
              icon={<Icon />}
              label={shortLabel(v)}
              badge={badge}
              accentBadge={accentBadge}
            />
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Labels courts pour la BottomNav (les labels complets dépassent souvent).
 */
function shortLabel(v: View): string {
  if (v === 'investors-directory') return 'Investors';
  if (v === 'founders-inbox') return 'Inbox';
  return VIEW_LABEL[v];
}

function Tab({ active, onClick, icon, label, badge, accentBadge }: {
  active?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  accentBadge?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'relative flex flex-col items-center justify-center gap-1 py-2.5 transition active:scale-95 ' +
        (active ? 'text-founders-deep' : 'text-ink-muted')
      }
      aria-current={active ? 'page' : undefined}
    >
      <span className={'w-6 h-6 grid place-items-center ' + (active ? 'text-founders-deep' : 'text-ink-muted')}>
        {icon}
      </span>
      <span className={'text-[10.5px] font-medium ' + (active ? 'text-founders-deep' : 'text-ink-muted')}>{label}</span>
      {typeof badge === 'number' && badge > 0 && (
        <span
          className={
            'absolute top-1.5 right-[calc(50%-1.5rem)] min-w-[16px] h-[16px] px-1 grid place-items-center rounded-full text-[9.5px] font-bold text-white num ' +
            (accentBadge ? 'bg-amber' : 'bg-founders')
          }
        >{badge > 99 ? '99+' : badge}</span>
      )}
    </button>
  );
}
